import cn from 'classnames';

import { useInt } from 'hooks/useInt';

import { CompanyRecordCard } from 'ui/molecules/CompanyRecordCard/CompanyRecordCard';
import { DifferenceSection as DifferenceSectionBase } from 'ui/organisms/DifferenceSection/DifferenceSection';
import type { DifferenceItem } from 'ui/organisms/DifferenceSection/DifferenceSection.types';

import styles from './DifferenceSection.module.scss';

type DifferenceSectionProps = {
  className?: string;
};

type Translate = (key: string) => string;

const SOURCES: { flag: string; name: string }[] = [
  { flag: '🇬🇧', name: 'Companies House' },
  { flag: '🇩🇪', name: 'Handelsregister' },
  { flag: '🇫🇷', name: 'Registre National des Entreprises' },
  { flag: '🇳🇱', name: 'Kamer van Koophandel' },
  { flag: '🇮🇹', name: 'Registro delle Imprese' },
  { flag: '🇵🇱', name: 'Krajowy Rejestr Sądowy' },
  { flag: '🇸🇬', name: 'ACRA' },
  { flag: '🇦🇺', name: 'ASIC' },
  { flag: '🇺🇸', name: 'Secretaries of State' },
  { flag: '🇮🇳', name: 'Ministry of Corporate Affairs' },
  { flag: '🇪🇸', name: 'Registro Mercantil' },
  { flag: '🇭🇰', name: 'Companies Registry' },
];

const SourcesCard = () => {
  const { t } = useInt();

  return (
    <div className={styles.card}>
      <div className={styles.cardHead}>
        <b>{t('Official sources')}</b>
        <span className={styles.cardMeta}>{t('excerpt · 400+ connected')}</span>
      </div>

      <div className={styles.sourcesGrid}>
        {SOURCES.map((source) => (
          <span key={source.name} className={styles.sourceItem}>
            <span className={styles.sourceFlag}>{source.flag}</span>
            {source.name}
          </span>
        ))}
      </div>

      <div className={styles.cardFoot}>
        <span className={styles.tag}>{t('+ hundreds more, on every continent')}</span>
      </div>
    </div>
  );
};

const GRANTS: { num: string; word: string; sub: string }[] = [
  { num: '01', word: 'Embed', sub: 'inside your product, app or platform' },
  { num: '02', word: 'Redistribute', sub: 'onward to your own clients' },
  { num: '03', word: 'White-label', sub: 'feeds delivered under your brand' },
  { num: '04', word: 'Resell', sub: 'with terms scoped to your use case' },
  { num: '05', word: 'Train AI', sub: 'licensed for model training and grounding' },
];

const LicensingCard = () => {
  const { t } = useInt();

  return (
    <div className={styles.card}>
      <div className={styles.cardHead}>
        <b>{t('Licence grants')}</b>
      </div>

      <div className={styles.grants}>
        {GRANTS.map((grant) => (
          <div key={grant.num} className={styles.grantRow}>
            <span className={styles.grantNum}>{grant.num}</span>
            <span className={styles.grantWord}>{t(grant.word)}</span>
            <span className={styles.grantSub}>{t(grant.sub)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const FILING_CHIPS = [
  'Legal name',
  'Address',
  'Legal form',
  'Registration number',
  'Status',
  'Officers',
  'Shareholders',
];

const ADDED_CHIPS = [
  'Industry tags',
  'Website',
  'Estimates',
  'Digitised financials',
  'Corporate linkage',
  'Social media',
];

const EnrichmentCard = () => {
  const { t } = useInt();

  return (
    <div className={styles.card}>
      <div className={styles.enrZone}>
        <div className={styles.enrLabel}>{t('The filing gives you')}</div>
        <div className={styles.enrChips}>
          {FILING_CHIPS.map((chip) => (
            <span key={chip} className={styles.enrChip}>
              {t(chip)}
            </span>
          ))}
        </div>
      </div>

      <div className={styles.enrArrow}>{t('we add')}</div>

      <div className={styles.enrZone}>
        <div className={styles.enrChips}>
          {ADDED_CHIPS.map((chip) => (
            <span key={chip} className={cn(styles.enrChip, styles.enrChipAdd)}>
              + {t(chip)}
            </span>
          ))}
        </div>
      </div>

      <div className={cn(styles.enrZone, styles.enrZoneResult)}>
        <div className={styles.enrLabel}>{t('The result')}</div>
        <p className={styles.enrResult}>
          {t('One complete record per company — entity-resolved, standardised and mapped across sources.')}
        </p>
      </div>
    </div>
  );
};

const TIMELINE: { year: string; text: string }[] = [
  { year: '2015', text: 'First direct registry pipelines go live' },
  { year: '2019', text: 'Filing archive reaches a full decade per company' },
  { year: '2022', text: 'Coverage passes 200 countries and territories' },
  { year: '2024', text: '600M+ company profiles, matched and deduplicated' },
  { year: '2026', text: 'Regis — the dataset becomes conversational' },
];

const TrackRecordCard = () => {
  const { t } = useInt();

  return (
    <div className={styles.card}>
      <div className={styles.timeline}>
        {TIMELINE.map((row) => (
          <div key={row.year} className={styles.timelineRow}>
            <span className={styles.timelineYear}>{row.year}</span>
            <p className={styles.timelineText}>{t(row.text)}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

const getItems = (t: Translate): DifferenceItem[] => [
  {
    number: '01',
    title: t('We source data directly from 400+ government registries.'),
    description: (
      <p>
        {t(
          'We maintain direct connections to each official registry and sync on its publication schedule — daily in most major markets. Records flow from the filing into our platform, API and feeds through one verified pipeline.',
        )}
      </p>
    ),
    media: <SourcesCard />,
  },
  {
    number: '02',
    title: t('Every record carries its source and timestamp.'),
    description: (
      <p>
        {t(
          'Every record, under the hood: the filing it came from, when it was filed, and when we last verified it. That audit trail exports with the data — so when your regulator or your client asks “where did this come from?”, the answer is already attached.',
        )}
      </p>
    ),
    media: <CompanyRecordCard />,
  },
  {
    number: '03',
    title: t('Reseller and redistribution rights, built into the terms.'),
    description: (
      <p>
        {t(
          'What you’re allowed to do with data matters as much as the data itself. Incumbent contracts are built to restrict; ours are built for platforms that embed, redistribute and resell. You bring the use case — the terms follow it.',
        )}
      </p>
    ),
    media: <LicensingCard />,
  },
  {
    number: '04',
    title: t('Every filing enriched with firmographics and entity resolution.'),
    description: (
      <p>
        {t(
          'A raw filing isn’t usable data — names vary, entities duplicate, context is missing. Before a record reaches you, we match it, deduplicate it and complete it with firmographics, so it drops into your CRM, warehouse or model ready to work.',
        )}
      </p>
    ),
    media: <EnrichmentCard />,
  },
  {
    number: '05',
    title: t('Ten years of experience turning registry filings into usable data.'),
    description: (
      <p>
        {t(
          'What ten years of building at the source looks like in practice: pipelines into 400+ registries, a decade of filing history per company, and the operational scar tissue of keeping it all current — groundwork a new entrant can’t shortcut.',
        )}
      </p>
    ),
    media: <TrackRecordCard />,
  },
];

export const DifferenceSection = ({ className }: DifferenceSectionProps) => {
  const { t } = useInt();

  return <DifferenceSectionBase title={t('What makes us different.')} items={getItems(t)} className={className} />;
};
